import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import {
  createFixedAsset,
  listFixedAssets,
  deleteFixedAsset,
} from "../../../Utility/Api";
import { WarehouseContext } from "../../../Utility/UserContext";

const Assets = () => {
  const { showLoader, hideLoader, UserItem, shopItem } = useContext(WarehouseContext);

  const [assetsList, setAssetsList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bankBalance, setBankBalance] = useState(null);
  const [form, setForm] = useState({
    title: "",
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    remark: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const shop_id = shopItem?.shop_id;

  useEffect(() => {
    if (shop_id) fetchAssets();
    // eslint-disable-next-line
  }, [shop_id]);

  const fetchAssets = async () => {
    try {
      showLoader();
      const res = await listFixedAssets({ shop_id });
      if (res.data?.status === "success") {
        setAssetsList(res.data.data || []);
      }
    } catch (e) {
      toast.error("Failed to load assets");
    } finally {
      hideLoader();
    }
  };

  const totalAssets = assetsList.reduce((s, a) => s + parseFloat(a.amount || 0), 0);

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Title is required";
    if (!form.date) e.date = "Date is required";
    if (!form.amount || isNaN(form.amount) || parseFloat(form.amount) <= 0)
      e.amount = "Valid positive amount is required";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        shop_id,
        created_by: UserItem?.user_id,
      };
      const res = await createFixedAsset(payload);
      if (res.data?.status === "success") {
        toast.success("Asset recorded successfully");
        setShowModal(false);
        setForm({ title: "", date: new Date().toISOString().slice(0, 10), amount: "", remark: "" });
        setErrors({});
        setBankBalance(null);
        fetchAssets();
      } else {
        const msg = res.data?.message || "Failed to create asset";
        toast.error(msg);
        if (res.data?.bank_balance !== undefined) {
          setBankBalance(res.data.bank_balance);
        }
      }
    } catch (err) {
      const msg = err.response?.data?.message || "An error occurred";
      toast.error(msg);
      if (err.response?.data?.bank_balance !== undefined) {
        setBankBalance(err.response.data.bank_balance);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      showLoader();
      const res = await deleteFixedAsset(deleteTarget.id);
      if (res.data?.status === "success") {
        toast.success("Asset deleted and ledger reversed");
        setShowDeleteModal(false);
        setDeleteTarget(null);
        fetchAssets();
      } else {
        toast.error(res.data?.message || "Delete failed");
      }
    } catch (e) {
      toast.error("Failed to delete asset");
    } finally {
      hideLoader();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    if (name === "amount" || name === "date") setBankBalance(null);
  };

  return (
    <div className="page-content">
      {/* <div className="page-wrappers"> */}
      {/* ── Page Header ───────────────────────────────────── */}
      <div
        className="d-flex align-items-center justify-content-between mb-4 px-2 py-3 rounded-3 shadow-sm"
        style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 100%)" }}
      >
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{ width: 46, height: 46, background: "rgba(255,255,255,0.15)" }}
          >
            <i className="bi bi-pc-display text-white fs-5" />
          </div>
          <div>
            <h5 className="mb-0 text-white fw-bold">Fixed Assets</h5>
            <small className="text-white-50">Manage company assets — Laptops, Machines, Vehicles, etc.</small>
          </div>
        </div>
        <button
          className="btn btn-light btn-sm fw-semibold d-flex align-items-center gap-2 shadow-sm"
          onClick={() => { setShowModal(true); setErrors({}); setBankBalance(null); }}
        >
          <i className="bi bi-plus-circle" /> Add Asset
        </button>
      </div>

      {/* ── Summary Cards ─────────────────────────────────── */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-md-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderLeft: "4px solid #2d6a9f" }}>
            <div className="card-body d-flex align-items-center gap-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48, background: "#e8f0fb" }}
              >
                <i className="bi bi-archive text-primary fs-5" />
              </div>
              <div>
                <div className="text-muted small">Total Assets</div>
                <div className="fw-bold fs-5">{assetsList.length}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-md-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderLeft: "4px solid #198754" }}>
            <div className="card-body d-flex align-items-center gap-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: 48, height: 48, background: "#d1f4e0" }}
              >
                <i className="bi bi-currency-rupee text-success fs-5" />
              </div>
              <div>
                <div className="text-muted small">Total Value</div>
                <div className="fw-bold fs-5">₹{totalAssets.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Assets Table ──────────────────────────────────── */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between py-3">
          <h6 className="mb-0 fw-bold text-dark">
            <i className="bi bi-list-ul me-2 text-primary" />Assets List
          </h6>
          <span className="badge bg-primary">{assetsList.length} Records</span>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover table-bordered mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th className="text-center" style={{ width: 50 }}>#</th>
                  <th>Title</th>
                  <th>Date</th>
                  <th className="text-end">Amount (₹)</th>
                  <th>Remark</th>
                  <th className="text-center" style={{ width: 90 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {assetsList.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-5">
                      <i className="bi bi-inbox fs-2 d-block mb-2 text-secondary" />
                      No assets recorded yet. Click <strong>Add Asset</strong> to begin.
                    </td>
                  </tr>
                ) : (
                  assetsList.map((asset, idx) => (
                    <tr key={asset.id}>
                      <td className="text-center text-muted">{idx + 1}</td>
                      <td>
                        <div className="fw-semibold text-dark">{asset.title || "-"}</div>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {asset.date || "-"}
                        </span>
                      </td>
                      <td className="text-end fw-bold text-success">
                        ₹{parseFloat(asset.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-muted small">{asset.remark || "-"}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Delete Asset"
                          onClick={() => { setDeleteTarget(asset); setShowDeleteModal(true); }}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {assetsList.length > 0 && (
                <tfoot>
                  <tr className="table-light">
                    <td colSpan={3} className="text-end fw-bold">Grand Total</td>
                    <td className="text-end fw-bold text-success">
                      ₹{totalAssets.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {/* ── Add Asset Modal ───────────────────────────────── */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
              {/* Header */}
              <div
                className="modal-header border-0 px-4 pt-4 pb-2"
                style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 100%)" }}
              >
                <div>
                  <h5 className="modal-title text-white fw-bold mb-0">
                    <i className="bi bi-plus-circle me-2" />Add New Asset
                  </h5>
                  <small className="text-white-50">Asset amount will be deducted from Bank/Cash</small>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                />
              </div>

              {/* Body */}
              <form onSubmit={handleSubmit}>
                <div className="modal-body px-4 py-3">
                  {/* Bank balance warning */}
                  {bankBalance !== null && (
                    <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-3">
                      <i className="bi bi-exclamation-triangle-fill text-warning" />
                      <span>
                        Available Bank Balance: <strong>₹{bankBalance.toLocaleString("en-IN")}</strong>.
                        Please enter an amount within this limit.
                      </span>
                    </div>
                  )}

                  {/* Title */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Asset Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.title ? "is-invalid" : ""}`}
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      placeholder="e.g. Laptop, Grinding Machine, Bike"
                    />
                    {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                  </div>

                  {/* Date */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Purchase Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control ${errors.date ? "is-invalid" : ""}`}
                      name="date"
                      value={form.date}
                      onChange={handleChange}
                    />
                    {errors.date && <div className="invalid-feedback">{errors.date}</div>}
                    <div className="form-text text-muted">
                      Capital/Bank funds must have been added before this date.
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Amount (₹) <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">₹</span>
                      <input
                        type="number"
                        className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                        name="amount"
                        value={form.amount}
                        onChange={handleChange}
                        placeholder="0.00"
                        min="0.01"
                        step="0.01"
                      />
                      {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                    </div>
                    <div className="form-text text-muted">
                      This amount will be deducted from Bank/Cash balance.
                    </div>
                  </div>

                  {/* Remark */}
                  <div className="mb-2">
                    <label className="form-label fw-semibold">Remark</label>
                    <textarea
                      className="form-control"
                      name="remark"
                      value={form.remark}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Optional notes about this asset..."
                    />
                  </div>
                </div>

                <div className="modal-footer border-0 px-4 pb-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary d-flex align-items-center gap-2"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <><span className="spinner-border spinner-border-sm" /> Saving…</>
                    ) : (
                      <><i className="bi bi-check-circle" /> Save Asset</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────── */}
      {showDeleteModal && deleteTarget && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-body text-center px-4 py-4">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
                  style={{ width: 56, height: 56, background: "#fde8e8" }}
                >
                  <i className="bi bi-trash text-danger fs-4" />
                </div>
                <h6 className="fw-bold mb-1">Delete Asset?</h6>
                <p className="text-muted small mb-0">
                  <strong>{deleteTarget.title}</strong> (₹{parseFloat(deleteTarget.amount || 0).toLocaleString("en-IN")}) will be deleted and the bank balance will be restored.
                </p>
              </div>
              <div className="modal-footer border-0 justify-content-center pb-4 gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm px-4"
                  onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                >
                  Cancel
                </button>
                <button className="btn btn-danger btn-sm px-4" onClick={handleDeleteConfirm}>
                  <i className="bi bi-trash me-1" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assets;
